import React, { useState, useRef, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import './MapFilterView.less';
import {
  dateAndTimeStringFromDate,
  getBrandColor,
  LocalizePropType,
  withTextLocalizer,
} from 'getaway-data-layer';
import { format, isSameDay, isAfter } from 'date-fns';
import { de } from 'date-fns/locale';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import { GA_TITLES } from '../../../../utils';
import BrandButton from '../../../components/BrandButton/BrandButton';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

type Props = {
  localize: LocalizePropType;
  backByOnly: boolean;
  isOpened: boolean;
  handleClose: () => void;
  isStartTimeUpdated: boolean;
  bookingRange: string;
  selectedStartDate: Date;
  onChangeStartDate: (date: Date) => void;
  selectedEndDate: Date;
  onChangeEndDate: (date: Date) => void;
  minStart: Date;
  minEnd: Date;
  maxEnd?: Date;
  onReset: () => void;
  onPressFilter: () => void;
  customError: string;
  nextBookingStart: Date | null;
  onPressVehicleTable: () => void;
};

const MapFilterView = ({
  localize,
  backByOnly,
  isOpened,
  handleClose,
  isStartTimeUpdated,
  bookingRange,
  selectedStartDate,
  onChangeStartDate,
  selectedEndDate,
  onChangeEndDate,
  minStart,
  minEnd,
  maxEnd,
  onReset,
  onPressFilter,
  customError,
  nextBookingStart,
  onPressVehicleTable,
}: Props) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);
  const startTimeDropdownRef = useRef(null);
  const endTimeDropdownRef = useRef(null);
  const datePickerRef = useRef(null);
  const navCalendarWrapperRef = useRef(null);
  const navBarRef = useRef(null);
  const bottomContainer = useRef(null);

  const [navHeight, setNavHeight] = useState(0);
  const [bottomContainerHeight, setBottomContainerHeight] = useState(0);

  const [dateRange, setDateRange] = useState({
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    key: 'selection',
  });

  // Separate effect to measure navigation height after render
  useEffect(() => {
    // Use setTimeout to ensure the measurement happens after render
    const timer = setTimeout(() => {
      if (navBarRef.current) {
        const height = navBarRef.current.offsetHeight;
        if (height > 0) {
          setNavHeight(height);
        }
      }
    }, 100);

    setShowDatePicker(false);
    return () => clearTimeout(timer);
  }, [isOpened]); // Re-measure when modal opens

  useEffect(() => {
    const timer = setTimeout(() => {
      if (bottomContainer.current) {
        const height = bottomContainer.current.offsetHeight;
        if (height > 0) {
          setBottomContainerHeight(height);
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isOpened]);

  // Pre-generate time options in 15-minute intervals (24-hour format)
  const timeOptions = React.useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        options.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return options;
  }, []);

  useEffect(() => {
    if (showStartTimePicker && startTimeDropdownRef.current) {
      const currentTimeStr = format(selectedStartDate, 'HH:mm');
      const selectedIndex = timeOptions.findIndex(
        (time) => time === currentTimeStr
      );
      if (selectedIndex > -1) {
        const optionHeight = 36; // Approximate height of each time option
        startTimeDropdownRef.current.scrollTop = selectedIndex * optionHeight;
      }
    }
  }, [showStartTimePicker, selectedStartDate, timeOptions]);

  useEffect(() => {
    if (showEndTimePicker && endTimeDropdownRef.current) {
      const currentTimeStr = format(selectedEndDate, 'HH:mm');
      const selectedIndex = timeOptions.findIndex(
        (time) => time === currentTimeStr
      );
      if (selectedIndex > -1) {
        const optionHeight = 36; // Approximate height of each time option
        endTimeDropdownRef.current.scrollTop = selectedIndex * optionHeight;
      }
    }
  }, [showEndTimePicker, selectedEndDate, timeOptions]);

  const handleDateRangeChange = (ranges) => {
    const { startDate, endDate } = ranges.selection;

    // Keep the time part from the original dates
    const newStartDate = new Date(startDate);
    newStartDate.setHours(
      selectedStartDate.getHours(),
      selectedStartDate.getMinutes(),
      0,
      0
    );

    const newEndDate = new Date(endDate);
    newEndDate.setHours(
      selectedEndDate.getHours(),
      selectedEndDate.getMinutes(),
      0,
      0
    );

    setDateRange({
      startDate: newStartDate,
      endDate: newEndDate,
      key: 'selection',
    });

    onChangeStartDate(newStartDate);
    onChangeEndDate(newEndDate);
  };

  const handleStartTimeChange = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const newDate = new Date(selectedStartDate);
    newDate.setHours(hours, minutes, 0, 0);
    onChangeStartDate(newDate);
    setShowStartTimePicker(false);
  };

  const handleEndTimeChange = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const newDate = new Date(selectedEndDate);
    newDate.setHours(hours, minutes, 0, 0);
    onChangeEndDate(newDate);
    setShowEndTimePicker(false);
  };

  // Effect to handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        startTimeRef.current &&
        !startTimeRef.current.contains(event.target)
      ) {
        setShowStartTimePicker(false);
      }
      if (endTimeRef.current && !endTimeRef.current.contains(event.target)) {
        setShowEndTimePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setDateRange({
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      key: 'selection',
    });
  }, [selectedStartDate, selectedEndDate]);

  // Check if a time should be disabled for start time
  const isStartTimeDisabled = (time) => {
    // Create a date object with the time string
    const [hours, minutes] = time.split(':').map(Number);
    const timeDate = new Date(selectedStartDate);
    timeDate.setHours(hours, minutes, 0, 0);

    // Check if the time is before minStart
    return isAfter(minStart, timeDate);
  };

  // Check if a time should be disabled for end time
  const isEndTimeDisabled = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const timeDate = new Date(selectedEndDate);
    timeDate.setHours(hours, minutes, 0, 0);

    // Check if the time is before minEnd or after maxEnd
    const isBeforeMinEnd = isAfter(minEnd, timeDate);
    const isAfterMaxEnd = maxEnd ? isAfter(timeDate, maxEnd) : false;

    // If on the same day, also check if end time is at or before start time
    let isBeforeOrEqualStartTime = false;
    if (isSameDay(selectedStartDate, selectedEndDate)) {
      const [startHours, startMinutes] = format(selectedStartDate, 'HH:mm')
        .split(':')
        .map(Number);

      // Check if end time is before or same as start time
      isBeforeOrEqualStartTime =
        hours < startHours || (hours === startHours && minutes <= startMinutes);
    }

    return isBeforeMinEnd || isAfterMaxEnd || isBeforeOrEqualStartTime;
  };

  return (
    <Modal open={isOpened} onClose={handleClose}>
      <div className="map-filter">
        {/* Nav + Calendar container */}
        <div
          className={`nav-calendar-container ${
            showDatePicker ? 'expanded' : ''
          }`}
          ref={navCalendarWrapperRef}
          style={{
            top: showDatePicker
              ? 0
              : `calc(100dvh - ${bottomContainerHeight}px - ${navHeight}px + 8px)`, // +8 px so border radius is visible
          }}
        >
          <div className="navigation-bar-container" ref={navBarRef}>
            <NavigationBar
              title={
                isStartTimeUpdated
                  ? bookingRange
                  : localize('map.filter.header')
              }
              titleStyles={{ fontSize: 22 }}
              description={
                showDatePicker
                  ? ''
                  : localize(
                      backByOnly
                        ? 'rental.backby.update.desc'
                        : 'map.filter.description'
                    )
              }
              onClose={handleClose}
              containerStyle={{
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                width: '100%',
              }}
              headerStyle={showDatePicker ? { paddingBottom: 0 } : {}}
            />
          </div>

          {/* Calendar */}
          <div className="calendar-container" ref={datePickerRef}>
            <DateRange
              ranges={[dateRange]}
              onChange={handleDateRangeChange}
              minDate={minStart}
              maxDate={maxEnd}
              months={3}
              direction="vertical"
              showMonthAndYearPickers={false}
              showDateDisplay={false}
              rangeColors={[getBrandColor()]}
              locale={de}
              scroll={{ enabled: true }}
            />
          </div>
        </div>

        {/* Bottom content area */}
        <div
          className="map-filter-content"
          ref={bottomContainer}
          style={{ height: customError ? 260 : 230 }}
        >
          <div className="map-filter-inputs">
            {/* Date Selection Area */}
            <div className="date-picker-container">
              <div className="date-picker-section">
                <div className="date-label">
                  {localize('map.filter.pickup')}
                </div>
                <div
                  className="date-display-box"
                  onClick={() => {
                    setShowDatePicker(true);
                  }}
                  data-testid="MapFilterView-pickupDate"
                >
                  <div className="date-value">
                    {format(selectedStartDate, 'EEE, d MMM', {
                      locale: de,
                    }).replace('.,', ',')}
                  </div>
                  <div
                    className="time-value"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowStartTimePicker(!showStartTimePicker);
                      setShowEndTimePicker(false);
                    }}
                    ref={startTimeRef}
                  >
                    {format(selectedStartDate, 'HH:mm', { locale: de })}
                    {showStartTimePicker && (
                      <div className="time-dropdown" ref={startTimeDropdownRef}>
                        {timeOptions.map((time) => (
                          <div
                            key={time}
                            className={`time-option ${
                              time === format(selectedStartDate, 'HH:mm')
                                ? 'selected'
                                : ''
                            } ${isStartTimeDisabled(time) ? 'disabled' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isStartTimeDisabled(time)) {
                                handleStartTimeChange(time);
                              }
                            }}
                          >
                            {time}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="date-picker-section">
                <div className="date-label">
                  {localize('map.filter.return')}
                </div>
                <div
                  className="date-display-box"
                  onClick={() => setShowDatePicker(true)}
                >
                  <div className="date-value">
                    {format(selectedEndDate, 'EEE, d MMM', {
                      locale: de,
                    }).replace('.,', ',')}
                  </div>
                  <div
                    className="time-value"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEndTimePicker(!showEndTimePicker);
                      setShowStartTimePicker(false);
                    }}
                    ref={endTimeRef}
                  >
                    {format(selectedEndDate, 'HH:mm', { locale: de })}
                    {showEndTimePicker && (
                      <div className="time-dropdown" ref={endTimeDropdownRef}>
                        {timeOptions.map((time) => (
                          <div
                            key={time}
                            className={`time-option ${
                              time === format(selectedEndDate, 'HH:mm')
                                ? 'selected'
                                : ''
                            } ${isEndTimeDisabled(time) ? 'disabled' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isEndTimeDisabled(time)) {
                                handleEndTimeChange(time);
                              }
                            }}
                          >
                            {time}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {customError && (
              <div className="map-filter-error">{customError}</div>
            )}
          </div>

          <div className="map-filter-bottom">
            {!backByOnly && (
              <div className="map-filter-reset" onClick={onReset}>
                {localize('map.filter.reset')}
              </div>
            )}

            {backByOnly && (
              <div
                className="booking-availability-container"
                onClick={onPressVehicleTable}
              >
                {nextBookingStart && (
                  <div>
                    {localize(
                      'rental.vehicle.booking.prelink',
                      dateAndTimeStringFromDate(nextBookingStart, localize)
                    )}
                  </div>
                )}
                <div className="booking-availability-link">
                  {localize('rental.vehicle.booking.link')}
                </div>
              </div>
            )}

            <BrandButton
              id={GA_TITLES.BOOKING.SET_FILTER}
              containerStyles={
                !backByOnly
                  ? {
                      width: 'calc(100% - 130px)',
                      position: 'unset',
                      left: 'unset',
                      bottom: 'unset',
                      fontSize: '15px',
                      // marginLeft: '30px',
                    }
                  : {}
              }
              title={localize(
                backByOnly ? 'rental.backby.button' : 'map.filter.check'
              )}
              disabled={!!customError}
              onClick={onPressFilter}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default withTextLocalizer(MapFilterView);
